import { lotusHealthSchemas } from '@lotushealth/lotushealth_js_sdk';
import type { RxChangeDenialReasonT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { z } from 'zod';

import { REFILL_DENIAL_REASON_LABELS, type RefillDenialReason } from '@/entities/rx';

/* ------------------------------------------------------------------------- */
/* Enum schemas — sourced from the SDK + entity contract so any new value    */
/* breaks at the type boundary first, never silently in a form.              */
/* ------------------------------------------------------------------------- */

const refillDenialReasonValues = Object.keys(REFILL_DENIAL_REASON_LABELS) as RefillDenialReason[];
const refillDenialReasonSchema = z.enum(
  refillDenialReasonValues as [RefillDenialReason, ...RefillDenialReason[]]
);

const rxChangeDenialReasonValues = Object.values(
  lotusHealthSchemas.RxChangeDenialReasonT
) as RxChangeDenialReasonT[];
const rxChangeDenialReasonSchema = z.enum(
  rxChangeDenialReasonValues as [RxChangeDenialReasonT, ...RxChangeDenialReasonT[]]
);

/* ------------------------------------------------------------------------- */
/* Common helpers                                                            */
/* ------------------------------------------------------------------------- */

/** Optional free-text field — trimmed, defaults to ``''`` so controlled inputs work. */
const optionalText = z.string().trim().default('');

/** Optional non-negative integer string (matches HTML ``<input type="number">``). */
const optionalIntegerString = optionalText.refine(
  (value) => value === '' || /^\d+$/.test(value),
  'Must be a non-negative integer'
);

const REQUIRED_DATE_OR_EMPTY = /^(?:\d{4}-\d{2}-\d{2})?$/;

/* ------------------------------------------------------------------------- */
/* 1. Refill approve                                                          */
/* ------------------------------------------------------------------------- */

export const refillApproveDraftSchema = z.object({
  // DoseSpot requires the refill count to be between 1 and 99.
  refills: z
    .string()
    .trim()
    .min(1, 'Refills is required')
    .regex(/^\d+$/, 'Refills must be a whole number')
    .refine((value) => {
      const parsed = Number.parseInt(value, 10);
      return parsed >= 1 && parsed <= 99;
    }, 'Refills must be between 1 and 99'),
  comment: optionalText,
  effectiveDate: optionalText.refine(
    (value) => REQUIRED_DATE_OR_EMPTY.test(value),
    'Effective date must be in YYYY-MM-DD format'
  ),
});
export type RefillApproveDraft = z.infer<typeof refillApproveDraftSchema>;

/* ------------------------------------------------------------------------- */
/* 2. Refill deny                                                             */
/* ------------------------------------------------------------------------- */

export const refillDenyDraftSchema = z.object({
  denialReason: refillDenialReasonSchema,
  note: optionalText,
});
export type RefillDenyDraft = z.infer<typeof refillDenyDraftSchema>;

/* ------------------------------------------------------------------------- */
/* 3. RxChange approve                                                        */
/* ------------------------------------------------------------------------- */

export const rxChangeApproveDraftSchema = z.object({
  selectedMedicationId: optionalIntegerString,
  priorAuthNumber: optionalText,
  priorAuthComment: optionalText,
  comment: optionalText,
});
export type RxChangeApproveDraft = z.infer<typeof rxChangeApproveDraftSchema>;

/* ------------------------------------------------------------------------- */
/* 4. RxChange deny                                                           */
/* ------------------------------------------------------------------------- */

export const rxChangeDenyDraftSchema = z.object({
  reason: rxChangeDenialReasonSchema,
  comment: optionalText,
});
export type RxChangeDenyDraft = z.infer<typeof rxChangeDenyDraftSchema>;

/* ------------------------------------------------------------------------- */
/* 5. Reconcile RxChange                                                      */
/* ------------------------------------------------------------------------- */

export const reconcileDraftSchema = z.object({
  userId: z.uuid('Patient user id must be a UUID'),
  referencedPrescriptionId: z.string().trim().min(1, 'Referenced prescription id is required'),
});
export type ReconcileDraft = z.infer<typeof reconcileDraftSchema>;

/* ------------------------------------------------------------------------- */
/* 6. Change RxChange patient                                                 */
/* ------------------------------------------------------------------------- */

export const changePatientDraftSchema = z.object({
  patientUserId: z.uuid('Patient user id must be a UUID'),
});
export type ChangePatientDraft = z.infer<typeof changePatientDraftSchema>;
